/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, getProperties } from '@ember/object';
import { isEmpty, isNone } from '@ember/utils';
import { ComponentLike } from '@glint/template';
import AccountModel, { DataCenter } from 'cio/models/account';
import ApiService from 'cio/services/api';
import AuthService from 'cio/services/auth';
import MomentService from 'cio/services/moment';
import ReleaseNotificationService from 'cio/services/release-notification';
import { dropTask } from 'ember-concurrency';
import { Data as AnalyticsData } from 'cio/utils/analytics/transforms';
import SupportForm from 'cio/components/support-form';
import FeedbackForm from 'cio/components/feedback-form';
import SSORequestProvider from 'cio/components/sso-feedback-form';
import FormsSuggestion from 'cio/components/forms/suggestion-form';
import BasicMergeFeedback from 'cio/components/merge/feedback-form';
import DataCampaignsTriggerFeedback from 'cio/components/campaigns/trigger-type-selector/data-campaigns-feedback-form';
import DataCampaignsWorkflowFeedback from 'cio/components/visual-workflow-builder-v2/add-actions-panel/build/data-campaigns-workflow-feedback-form';
import LanguageAttributeFeedback from 'cio/components/language-attribute-feedback-form';
import BatchUpdateActionFeedback from 'cio/components/action-composer/types/batch-update/feedback-form';
import BatchUpdatePeopleLimitFeedback from 'cio/components/action-composer/types/batch-update/people-limit-feedback-form';
import DisableExperimentalFeatureFeedback from 'cio/components/disable-experimental-feature-flag-feedback-form';
import SwitchToCustomEditorFeedback from 'cio/components/action-composer/types/push/rich-editor/switch-to-custom-editor-feedback-form';
import LegacyCampaignExitOptionsFeedback from 'cio/components/campaigns/campaign-exit-setup/legacy-options-feedback-form';
import ObjectsRequestAccess from 'cio/components/objects/request-access-form';
import ObjectFeedback from 'cio/components/objects/feedback-form';
import PlanUpsell from 'cio/components/upsell-modal/upsell-modal-body';
import TrialUpsell from 'cio/components/upsell-modal/upsell-modal-body';
import RequestLanguages from 'cio/components/subscription-center/request-languages-form';
import VisualEditorFeedback from 'cio/components/action-composer-v2/visual-editor/feedback-form';
import FakeDoorFeedback from 'cio/components/setup-guide/fake-door-form';

const HELP_FORM_COMPONENT_PATH_MAP: Record<keyof typeof ENDPOINTS, ComponentLike<any>> = {
  support: SupportForm,
  feedback: FeedbackForm,
  ssoRequestProvider: SSORequestProvider,
  formsSuggestion: FormsSuggestion,
  basicMergeFeedback: BasicMergeFeedback,
  dataCampaignsTriggerFeedback: DataCampaignsTriggerFeedback,
  dataCampaignsWorkflowFeedback: DataCampaignsWorkflowFeedback,
  languageAttributeFeedback: LanguageAttributeFeedback,
  batchUpdateActionFeedback: BatchUpdateActionFeedback,
  batchUpdatePeopleLimitFeedback: BatchUpdatePeopleLimitFeedback,
  disableExperimentalFeatureFeedback: DisableExperimentalFeatureFeedback,
  switchToCustomEditorFeedback: SwitchToCustomEditorFeedback,
  legacyCampaignExitOptionsFeedback: LegacyCampaignExitOptionsFeedback,
  objectsRequestAccess: ObjectsRequestAccess,
  objectFeedback: ObjectFeedback,
  planUpsell: PlanUpsell,
  trialUpsell: TrialUpsell,
  requestLanguages: RequestLanguages,
  visualEditorFeedback: VisualEditorFeedback,
  fakeDoorFeedback: FakeDoorFeedback,
};

const ENDPOINTS = {
  support: 'support',
  feedback: 'feedback',
  ssoRequestProvider: 'feedback',
  formsSuggestion: 'feedback',
  basicMergeFeedback: 'feedback',
  dataCampaignsTriggerFeedback: 'feedback',
  dataCampaignsWorkflowFeedback: 'feedback',
  languageAttributeFeedback: 'feedback',
  batchUpdateActionFeedback: 'feedback',
  batchUpdatePeopleLimitFeedback: 'feedback',
  disableExperimentalFeatureFeedback: 'feedback',
  switchToCustomEditorFeedback: 'feedback',
  legacyCampaignExitOptionsFeedback: 'feedback',
  objectsRequestAccess: 'feedback',
  objectFeedback: 'feedback',
  planUpsell: 'premium',
  trialUpsell: 'sales',
  requestLanguages: 'feedback',
  visualEditorFeedback: 'feedback',
  fakeDoorFeedback: 'feedback',
};

export interface HelpModalComponentArgs {
  replyTo: HelpModal['replyTo'];
  userEmail: HelpModal['userEmail'];
  userFirstName: HelpModal['userFirstName'];
  userLastName: HelpModal['userLastName'];
  onClose: HelpModalArgs['onClose'];
  onSubmit: HelpModal['submitFeedback']['perform'];
  isSubmitting: boolean;
  title?: HelpModalArgs['title'];
  disabled: boolean;
}

interface HelpModalArgs {
  account: AccountModel;
  title?: string;
  helpType: keyof typeof HELP_FORM_COMPONENT_PATH_MAP;
  onClose: (...args: unknown[]) => unknown;
  afterSubmit?: (formValues: Record<string, any>) => unknown;
  analyticsData?: AnalyticsData;
  disabled?: boolean;
}

export default class HelpModal extends Component<HelpModalArgs> {
  @service api!: ApiService;
  @service auth!: AuthService;
  @service('moment' as any) momentService!: MomentService;

  @service releaseNotification!: ReleaseNotificationService;

  // Session Information
  @tracked browser = window.navigator.userAgent;

  // Internal API
  @tracked isSubmitted = false;

  @tracked actualHelpType: keyof typeof HELP_FORM_COMPONENT_PATH_MAP;
  @tracked replyTo = '';

  supportPersonImage = new URL('cio/assets/images/support-person.png', import.meta.url).toString();
  cheerImage = new URL('cio/assets/images/cheer.png', import.meta.url).toString();
  handsImage = new URL('cio/assets/images/hands.png', import.meta.url).toString();
  gratitudeImage = new URL('cio/assets/images/gratitude.png', import.meta.url).toString();

  @readOnly('args.account.email') accountEmail!: string;
  @readOnly('args.account.id') accountId!: string;
  @readOnly('args.account.createdAt') accountCreatedAt!: number;
  @readOnly('auth.user.firstName') userFirstName!: string;
  @readOnly('auth.user.lastName') userLastName!: string;
  @readOnly('auth.user.email') userEmail!: string;

  get flyModalTitle() {
    if (this.isSubmitted || this.actualHelpType === 'support' || isEmpty(this.args.title)) {
      return null;
    }

    if (this.actualHelpType === 'feedback') {
      return this.args.title ?? 'Send us feedback';
    }

    return this.args.title;
  }

  get helpFormComponent() {
    return HELP_FORM_COMPONENT_PATH_MAP[this.actualHelpType];
  }

  get disabled() {
    return this.args?.disabled ?? false;
  }

  constructor(owner: unknown, args: HelpModalArgs) {
    super(owner, args);

    this.actualHelpType = args.helpType;
    this.replyTo = this.userEmail;
  }

  submitFeedback = dropTask(
    async (formValues: Record<string, any>, customThanksSection = false, skipSendingFeedback = false) => {
      if (isEmpty(this.replyTo)) {
        this.replyTo = this.accountEmail;
      }

      let { original } = this.releaseNotification;

      formValues.consent_to_access = formValues.consentToAccess || false;
      delete formValues.consentToAccess;

      let data = {
        ...formValues,
        ...getProperties(this, 'replyTo', 'browser', 'accountEmail', 'accountId', 'userFirstName', 'userLastName'),
        url: window.location.href,

        // not setting on component init to avoid mis-matched values if window browser is resized
        windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
        version: original?.version,
      };

      if (!isNone(original)) {
        // @ts-ignore: incomplete momentService typings ("moment" property)
        data.releaseDate = this.momentService.moment(original.createdAt * 1000).calendar();
      }

      if (!skipSendingFeedback) {
        await this.api.post(ENDPOINTS[this.actualHelpType], { data, pathScope: 'account', dataCenter: DataCenter.US });
      }

      this.isSubmitted = !customThanksSection;
      if (this.args.afterSubmit) {
        await this.args.afterSubmit(formValues);
      }
    },
  );

  @action
  switchToSupport() {
    this.actualHelpType = 'support';
    this.isSubmitted = false;
  }
}
